import { useTranslation } from 'react-i18next'
import { MarketingPage } from '@/components/marketing/marketing-page'
import { ProseSection, ProseParagraphs } from '@/components/marketing/prose-section'
import { MarketingCta } from '@/components/marketing/marketing-cta'
import { RelatedLinks } from '@/components/marketing/related-links'
import { buildRelatedLinks, type MarketingPageKey } from '@/components/marketing/links'
import { arrayKey } from '@/components/marketing/i18n-array'
import { JsonLd } from '@/components/seo/json-ld'
import { breadcrumbSchema } from '@/components/marketing/marketing-schema'

interface UseCasePageProps {
  readonly contentKey: MarketingPageKey
  readonly path: string
  readonly relatedKeys: readonly MarketingPageKey[]
}

/**
 * Shared template for /use-cases/* persona pages. Each page expands the matching
 * landing persona into a problem → workflow → outcome narrative, all sourced
 * from `marketing.<contentKey>.*` i18n keys.
 */
export function UseCasePage({ contentKey, path, relatedKeys }: UseCasePageProps) {
  const { t } = useTranslation()
  const body = (name: string) => arrayKey(t, `marketing.${contentKey}.${name}.body`)

  return (
    <MarketingPage
      seoTitle={t(`marketing.${contentKey}.seo.title`)}
      seoDescription={t(`marketing.${contentKey}.seo.description`)}
      path={path}
      eyebrow={t(`marketing.${contentKey}.eyebrow`)}
      heading={t(`marketing.${contentKey}.h1`)}
      lead={t(`marketing.${contentKey}.lead`)}
    >
      <JsonLd data={breadcrumbSchema(contentKey, [{ name: 'Use cases', path: '/' }])} />

      <ProseSection title={t(`marketing.${contentKey}.problem.title`)}>
        <ProseParagraphs paragraphs={body('problem')} />
      </ProseSection>
      <ProseSection title={t(`marketing.${contentKey}.workflow.title`)}>
        <ProseParagraphs paragraphs={body('workflow')} />
      </ProseSection>
      <ProseSection title={t(`marketing.${contentKey}.outcome.title`)}>
        <ProseParagraphs paragraphs={body('outcome')} />
      </ProseSection>

      <MarketingCta
        title={t(`marketing.${contentKey}.cta.title`)}
        body={t(`marketing.${contentKey}.cta.body`)}
        buttonLabel={t(`marketing.${contentKey}.cta.button`)}
        to="/"
      />
      <RelatedLinks
        heading={t('marketing.common.relatedHeading')}
        links={buildRelatedLinks(t, contentKey, relatedKeys)}
      />
    </MarketingPage>
  )
}
